import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    MenuItem,
    Button,
    Paper,
    CircularProgress,
    Typography,
    Box,
    Grid,
    Divider,
} from "@mui/material";
import NavBar from "../navbar";
import { useParams, useNavigate } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import { fromUtcInput, toUtcInput } from "../utc";

const EditBalance = () => {
    const { balance_id } = useParams();
    const navigate = useNavigate();

    const [balanceData, setBalanceData] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const authHeaders = {
            "Content-Type": "application/json",
            "user-authorization": getSession().token,
        };

        const fetchData = async () => {
            try {
                const [balanceRes, plansRes] = await Promise.all([
                    fetch(config.apiBaseUrl + `/admin/balances?balance_id=${balance_id}`, { method: "GET", headers: authHeaders }),
                    fetch(config.apiBaseUrl + "/admin/plans", { method: "GET", headers: authHeaders }),
                ]);
                if (!balanceRes.ok) {
                    throw new Error(`Error: ${balanceRes.status} ${balanceRes.statusText}`);
                }
                const balanceJson = await balanceRes.json();
                setBalanceData(balanceJson[0]);
                if (plansRes.ok) {
                    setPlans(await plansRes.json());
                }
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [balance_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setBalanceData({ ...balanceData, [field]: value });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                id: balanceData.id,
                points: balanceData.points,
                purchase_credits: balanceData.purchase_credits,
                plan_id: balanceData.plan?.id,
            };
            if (balanceData.expires_at) {
                payload.expires_at = fromUtcInput(balanceData.expires_at);
            }
            const response = await fetch(config.apiBaseUrl + `/admin/balances`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("Balance updated successfully!");
            navigate(`/balances`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to save balance: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this balance? ALL tokens sharing it will be deleted too (cascade). This cannot be undone.")) return;

        try {
            const response = await fetch(config.apiBaseUrl + `/admin/balances?balance_id=${balance_id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("Balance deleted successfully!");
            navigate(`/balances`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to delete balance: ${err.message}`);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Edit Balance
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        Balance Fields
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Balance ID"
                                value={balanceData.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Points (subscription)"
                                type="number"
                                value={balanceData.points ?? 0}
                                onChange={(e) => handleChange("points", parseInt(e.target.value, 10) || 0)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Purchase credits (topup / debt)"
                                type="number"
                                helperText="Can be negative — a negative balance is a debt recovered on the next payment."
                                value={balanceData.purchase_credits ?? 0}
                                onChange={(e) => handleChange("purchase_credits", parseInt(e.target.value, 10) || 0)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                margin="normal"
                                label="Plan (shared by all tokens)"
                                value={balanceData.plan?.id || ""}
                                onChange={(e) => {
                                    const plan = plans.find((p) => p.id === e.target.value);
                                    handleChange("plan", plan || balanceData.plan);
                                }}
                            >
                                {plans.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.name} ({p.usage_context})
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Expires At (UTC)"
                                type="datetime-local"
                                value={toUtcInput(balanceData.expires_at)}
                                onChange={(e) => {
                                    const value = fromUtcInput(e.target.value);
                                    if (value) handleChange("expires_at", value);
                                }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Tokens on this balance"
                                value={balanceData.token_count ?? 0}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Owner & Subscription
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="User ID"
                                value={balanceData.user?.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="User Email"
                                value={balanceData.user?.email || ""}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Subscription ID"
                                value={balanceData.subscription_id || "—"}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditBalance;
